<template>
  <b-card class="p-1">
    <div class="d-flex justify-content-between align-items-center mb-1">
      <div class="d-flex align-items-center">
        <b-form-group class="mb-0 mr-3 lang-select">
          <label for="language-select" class="small">Język:</label>
          <v-select
            id="language-select"
            label="text"
            placeholder="Wybierz język"
            v-model="selectedLanguage"
            :clearable="false"
            :deselectFromDropdown="false"
            :options="availableLanguages"
            @input="fetchFooterLinks" />
        </b-form-group>

        <b-form-group class="mb-0 mr-3 section-select">
          <label for="section-select" class="small">Sekcja:</label>
          <v-select
            id="section-select"
            label="text"
            value="value"
            placeholder="Wszystkie sekcje"
            v-model="selectedSection"
            :clearable="true"
            :deselectFromDropdown="false"
            :options="availableSections"
            @input="filterBySection" />
        </b-form-group>

        <b-form-group class="mb-0 w-auto">
          <label for="filterInput" class="small">Wyszukaj:</label>
          <b-input-group>
            <b-form-input id="filterInput" v-model="filter" type="search" placeholder="Wyszukaj po tytule" />
            <b-input-group-append>
              <b-button :disabled="!filter" @click="filter = ''"> Wyczyść </b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </div>

      <b-button
        :to="{ name: 'footer-management-create' }"
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        type="submit"
        variant="primary"
        class="btn">
        Dodaj
      </b-button>
    </div>

    <b-table
      responsive
      bordered
      striped
      hover
      :items="filteredFooterLinks"
      :fields="fields"
      :filter="filter"
      :filter-included-fields="filterOn">
      <template #cell(parent)="row">
        <b-link :to="{ name: 'footer-management-edit', params: { id: row.item.parent.id } }" v-if="row.item.parent">
          {{ row.item.parent.title }}
        </b-link>
        <span v-else class="text-muted">-</span>
      </template>

      <template #cell(section_identifier)="row">
        <b-badge variant="info">{{ getSectionName(row.item.section_identifier) }}</b-badge>
      </template>

      <template #cell(link_type)="row">
        <b-badge :variant="row.item.link_type === 'internal' ? 'success' : 'warning'">
          {{ row.item.link_type === 'internal' ? 'Wewnętrzny' : 'Zewnętrzny' }}
        </b-badge>
      </template>

      <template #cell(url)="row">
        <span v-if="row.item.link_type === 'internal'">{{ row.item.route_name }}</span>
        <a v-else :href="row.item.external_url" target="_blank" class="text-primary">
          {{ row.item.external_url }}
        </a>
      </template>

      <template #cell(is_active)="row">
        <b-badge :variant="row.item.is_active ? 'success' : 'danger'">
          {{ row.item.is_active ? 'Aktywny' : 'Nieaktywny' }}
        </b-badge>
      </template>

      <template #cell(actions)="row">
        <div class="d-flex flex-column justify-content-center align-items-center">
          <b-button
            :to="{ name: 'footer-management-edit', params: { id: row.item.id } }"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            type="submit"
            variant="primary"
            class="btn table-btn"
            size="sm">
            Edytuj
          </b-button>
          <b-button class="mt-1 table-btn" variant="danger" size="sm" @click="deleteFooterLink(row.item)">
            Usuń
          </b-button>
        </div>
      </template>
    </b-table>
  </b-card>
</template>

<script>
import NotificationMixin from '@/mixins/NotificationMixin';
import { all, destroy } from '@/http/footer-management';
import { LanguageFormatter } from '@/formatters/LanguageFormatter';
import { getLanguages } from '@/constants/languages';
import { FOOTER_SECTIONS_OPTIONS } from '@/constants/enums/footer-sections';

export default {
  mixins: [NotificationMixin],

  data() {
    return {
      footerLinks: [],
      filteredFooterLinks: [],
      selectedLanguage: { text: 'Polski', value: 'pl' },
      selectedSection: null,
      filter: '',
      filterOn: ['title'],

      availableLanguages: getLanguages().map((lang) => ({
        text: lang.value,
        value: lang.lang,
      })),

      availableSections: FOOTER_SECTIONS_OPTIONS,

      fields: [
        {
          key: 'title',
          label: 'Tytuł',
          sortable: true,
          sortDirection: 'desc',
        },
        {
          key: 'lang',
          label: 'Język',
          sortable: true,
          formatter: (val) => LanguageFormatter(val),
        },
        {
          key: 'parent',
          label: 'Link nadrzędny',
          sortable: false,
        },
        {
          key: 'section_identifier',
          label: 'Sekcja',
          sortable: true,
        },
        {
          key: 'link_type',
          label: 'Typ',
          sortable: true,
        },
        {
          key: 'url',
          label: 'URL / Nazwa podstrony',
          sortable: false,
        },
        {
          key: 'sort_order',
          label: 'Kolejność',
          sortable: true,
        },
        {
          key: 'is_active',
          label: 'Status',
          sortable: true,
        },
        { key: 'actions', label: 'Akcje', class: 'w-25' },
      ],
    };
  },

  async created() {
    await this.fetchFooterLinks();
  },

  methods: {
    async fetchFooterLinks() {
      try {
        const { data } = await all();
        this.footerLinks = data.data;
        this.filterBySection();
      } catch (error) {
        this.showErrorNotification(
          'Problem z pobraniem danych',
          'Wystąpił problem z pobraniem linków stopki. Skontaktuj się ze swoim developerem.',
        );
      }
    },

    filterBySection() {
      let filtered = this.footerLinks;

      // Filter by language
      if (this.selectedLanguage) {
        filtered = filtered.filter((link) => link.lang === this.selectedLanguage.value);
      }

      // Filter by section
      if (this.selectedSection) {
        filtered = filtered.filter((link) => link.section_identifier === this.selectedSection.value);
      }

      this.filteredFooterLinks = filtered;
    },

    getSectionName(sectionIdentifier) {
      const section = this.availableSections.find((s) => s.value === sectionIdentifier);
      return section ? section.text : sectionIdentifier;
    },

    async deleteFooterLink(footerLink) {
      this.$swal({
        title: 'Jesteś pewny?',
        text: 'Czy chcesz usunąć ten link stopki?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Usuń',
        cancelButtonText: 'Anuluj',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          destroy(footerLink.id)
            .then(() => {
              this.showSuccessNotification('Usunięto link', 'Link stopki został usunięty.');
              this.fetchFooterLinks();
            })
            .catch(() => {
              this.showErrorNotification(
                'Problem z usunięciem linku',
                'Wystąpił problem z usunięciem linku stopki. Skontaktuj się ze swoim developerem.',
              );
            });
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';

.table-btn {
  min-width: 80px;
}

.lang-select {
  min-width: 140px;
  width: auto;
}

.section-select {
  min-width: 220px;
  width: auto;
}
</style>
